import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createOrganization } from "../../api/api";
import { adoptAccount } from "../../lib/sessionActions";
import { Checkbox } from "../ui/checkbox";
import { FormattedBody } from "../ui/formatted-body";

export default function Submit({ formData, beliefs, termsAgreed, onBack, onSetUser }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [informationVerified, setInformationVerified] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError("");

        // Prepare organization data
        const orgData = {
            username: formData.username,
            password: formData.password,
            name: formData.name,
            email: formData.email,
            address: formData.address || "",
            phone: formData.phone || "",
            website: formData.website || "",
            bio: formData.bio || "",
            mission_statement: formData.mission_statement || "",
            pfp_url: formData.pfp_url || "",
            denomination: beliefs?.denomination || "",
            latitude: formData.latitude ?? null,
            longitude: formData.longitude ?? null,
            links: (formData.links || []).filter(link => link.url && link.url.trim() !== "" && link.label && link.label.trim() !== "")
        };

        try {
            const result = await createOrganization(orgData);

            if (result.error) {
                setError(result.error);
                setIsSubmitting(false);
            } else {
                // Update user state if onSetUser is provided
                const account = result.data
                    ? { ...result.data, type: result.data.type || "organization" }
                    : null;
                if (result.token && account) {
                    const adopted = await adoptAccount(account, result.token);
                    if (adopted.error) {
                        setError(adopted.error);
                        setIsSubmitting(false);
                        return;
                    }
                }
                if (onSetUser && account) {
                    onSetUser(account);
                }
                // Redirect to organization admin page
                navigate("/organization-admin");
            }
        } catch (err) {
            setError("Network error or server unavailable");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-text-primary mb-4">Review & Submit</h2>
            
            {/* Review Message */}
            <div className="bg-tertiary rounded-lg p-6 border border-border">
                <h3 className="text-lg font-semibold text-text-primary mb-3">Review Process</h3>
                <p className="text-text-secondary">
                    Your organization will be reviewed by our team to confirm its authenticity and alignment with Christian values. 
                    You will be notified via email once your organization has been accepted or if any additional information is needed.
                </p>
            </div>

            {/* Review Information */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-text-primary">Organization Information</h3>
                
                <div className="bg-tertiary rounded-lg p-6 border border-border space-y-4">
                    {/* Required Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-text-secondary">Organization Name</label>
                            <p className="text-text-primary mt-1">{formData.name || "Not provided"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-text-secondary">Username</label>
                            <p className="text-text-primary mt-1">{formData.username || "Not provided"}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-text-secondary">Email</label>
                            <p className="text-text-primary mt-1">{formData.email || "Not provided"}</p>
                        </div>
                    </div>

                    {/* Optional Fields */}
                    {(formData.bio || formData.mission_statement || formData.phone || formData.website || formData.address || formData.pfp_url) && (
                        <div className="pt-4 border-t border-border space-y-4">
                            {formData.pfp_url && (
                                <div>
                                    <label className="text-sm font-medium text-text-secondary">Logo URL</label>
                                    <p className="text-text-primary mt-1 break-all">{formData.pfp_url}</p>
                                </div>
                            )}
                            {formData.bio && (
                                <div>
                                    <label className="text-sm font-medium text-text-secondary">Description</label>
                                    <FormattedBody as="p" className="mt-1 text-text-primary">{formData.bio}</FormattedBody>
                                </div>
                            )}
                            {formData.mission_statement && (
                                <div>
                                    <label className="text-sm font-medium text-text-secondary">Mission Statement</label>
                                    <FormattedBody as="p" className="mt-1 text-text-primary">{formData.mission_statement}</FormattedBody>
                                </div>
                            )}
                            {(formData.phone || formData.website || formData.address) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {formData.phone && (
                                        <div>
                                            <label className="text-sm font-medium text-text-secondary">Phone</label>
                                            <p className="text-text-primary mt-1">{formData.phone}</p>
                                        </div>
                                    )}
                                    {formData.website && (
                                        <div>
                                            <label className="text-sm font-medium text-text-secondary">Website</label>
                                            <p className="text-text-primary mt-1 break-all">{formData.website}</p>
                                        </div>
                                    )}
                                    {formData.address && (
                                        <div className="md:col-span-2">
                                            <label className="text-sm font-medium text-text-secondary">Address</label>
                                            <p className="text-text-primary mt-1 whitespace-pre-wrap">{formData.address}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Links */}
                    {formData.links && Array.isArray(formData.links) && formData.links.length > 0 && formData.links.some(link => link.url && link.label) && (
                        <div className="pt-4 border-t border-border">
                            <label className="text-sm font-medium text-text-secondary mb-2 block">Links</label>
                            <div className="space-y-2">
                                {formData.links
                                    .filter(link => link.url && link.url.trim() !== "" && link.label && link.label.trim() !== "")
                                    .map((link, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <span className="text-text-primary font-medium">{link.label}:</span>
                                            <a 
                                                href={link.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-accent hover:underline break-all"
                                            >
                                                {link.url}
                                            </a>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Beliefs */}
                    {(beliefs?.denomination || beliefs?.niceneCreedAgreed) && (
                        <div className="pt-4 border-t border-border space-y-4">
                            {(beliefs?.denomination || beliefs?.baptism || beliefs?.communion || beliefs?.scriptureAuthority || beliefs?.churchGovernance || beliefs?.worshipStyle) && (
                                <>
                                    <h4 className="text-md font-semibold text-text-primary">Beliefs</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-text-secondary">Denomination</label>
                                            <p className="text-text-primary mt-1">{beliefs.denomination}</p>
                                        </div>
                                    </div>
                                </>
                            )}
                            {beliefs?.niceneCreedAgreed && (
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        checked={!!beliefs?.niceneCreedAgreed}
                                        disabled
                                    />
                                    <label className="text-sm text-text-secondary">
                                        {(formData?.name || 'your organization')} agrees with the Nicene Creed
                                    </label>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Terms Agreement */}
                    <div className="pt-4 border-t border-border">
                        <div className="flex items-center space-x-2">
                            <Checkbox checked={termsAgreed} disabled />
                            <label className="text-sm text-text-secondary">
                                I have read and agree to the terms and conditions
                            </label>
                        </div>
                    </div>

                    {/* Information Verification */}
                    <div className="pt-4 border-t border-border">
                        <div className="flex items-start space-x-3">
                            <Checkbox
                                id="information-verification"
                                checked={informationVerified}
                                onCheckedChange={setInformationVerified}
                                className="mt-1 h-5 w-5"
                            />
                            <label htmlFor="information-verification" className="text-sm text-text-secondary cursor-pointer">
                                I verify that all information provided is accurate and truthful. I understand that providing false or misleading information may result in the rejection of this organization application or removal from the platform.
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-error/20 border border-error rounded-lg p-4">
                    <p className="text-error">{error}</p>
                </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t border-border">
                <button
                    onClick={onBack}
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-secondary text-text-primary border border-border rounded-md hover:bg-tertiary transition disabled:opacity-50"
                >
                    Back
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !informationVerified}
                    className={`px-6 py-2 rounded-md transition ${
                        isSubmitting || !informationVerified
                            ? "bg-secondary text-text-secondary cursor-not-allowed opacity-50"
                            : "bg-accent text-text-on-accent hover:bg-accent-hover"
                    }`}
                >
                    {isSubmitting ? "Submitting..." : "Submit Organization"}
                </button>
            </div>
        </div>
    );
}

